import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_CATEGORIES_BY_ENTITY,
  ATTACHMENT_CATEGORY_LABELS,
  ATTACHMENT_ENTITY_LABELS,
  ATTACHMENT_MAX_MB,
  attachmentUrlNeedsRefresh,
  formatFileSize,
  type AttachmentCategory,
  type AttachmentDto,
  type AttachmentEntity,
} from '@performance-crm/shared';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { useId, useRef, useState, type ChangeEvent, type MouseEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { apiUrl } from '@/lib/api';
import { fmt } from '@/lib/format';
import { useDeleteAttachment, useUploadAttachment } from '@/lib/queries';

/** A qué se adjunta: una unidad, un proyecto, o en la ficha del lead el propio lead, su cliente o un proceso. */
export interface AttachmentTarget {
  entityType: AttachmentEntity;
  entityId: string;
  /** Texto del selector "Guardar en". */
  label: string;
  /** Texto corto para la etiqueta de cada archivo (por defecto el del tipo de entidad). */
  badge?: string;
}

const keyOf = (t: AttachmentTarget) => `${t.entityType}:${t.entityId}`;

/** Lista de archivos con subida (categoría + descripción) y borrado con confirmación en línea. */
export function AttachmentsPanel({
  items,
  isLoading,
  targets,
  canUpload,
  showTarget = false,
  refresh,
}: {
  items: AttachmentDto[] | undefined;
  isLoading: boolean;
  targets: AttachmentTarget[];
  canUpload: boolean;
  /** Mostrar en cada archivo a qué pertenece (ficha del lead: lead, cliente o proceso). */
  showTarget?: boolean;
  /** Vuelve a pedir la lista (enlaces firmados nuevos); se usa al abrir un enlace con más de 20 minutos. */
  refresh?: () => Promise<AttachmentDto[] | undefined>;
}) {
  const id = useId();
  const toast = useToast();
  const upload = useUploadAttachment();
  const remove = useDeleteAttachment();
  const inputRef = useRef<HTMLInputElement>(null);
  const first = targets[0];
  const [targetKey, setTargetKey] = useState(first ? keyOf(first) : '');
  const target = targets.find((t) => keyOf(t) === targetKey) ?? first;
  const categories = target ? ATTACHMENT_CATEGORIES_BY_ENTITY[target.entityType] : [];
  const [category, setCategory] = useState<AttachmentCategory>(categories[0] ?? 'OTRO');
  const [description, setDescription] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);

  const chooseTarget = (key: string) => {
    setTargetKey(key);
    const next = targets.find((t) => keyOf(t) === key);
    if (next) setCategory(ATTACHMENT_CATEGORIES_BY_ENTITY[next.entityType][0] ?? 'OTRO');
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !target) return;
    if (file.size > ATTACHMENT_MAX_MB * 1024 * 1024) {
      toast.error('Archivo demasiado grande', `El máximo es ${ATTACHMENT_MAX_MB} MB`);
      return;
    }
    try {
      await upload.mutateAsync({
        entityType: target.entityType,
        entityId: target.entityId,
        category,
        description: description.trim() || undefined,
        file,
      });
      toast.success('Archivo subido', file.name);
      setDescription('');
    } catch (err) {
      toast.error('No se pudo subir el archivo', (err as Error).message);
    }
  };

  const onDelete = async (a: AttachmentDto) => {
    try {
      await remove.mutateAsync(a.id);
      toast.success('Archivo borrado', a.fileName);
    } catch (err) {
      toast.error('No se pudo borrar', (err as Error).message);
    } finally {
      setConfirming(null);
    }
  };

  /**
   * Los enlaces firmados duran 30 minutos. Si el de este archivo ya tiene más de 20, se pide la lista de nuevo y se
   * abre el enlace fresco (ux-ficha-13). La pestaña se abre antes de esperar para que el navegador no la bloquee.
   */
  const freshUrl = async (a: AttachmentDto, kind: 'url' | 'downloadUrl') => {
    const items = await refresh!();
    const fresh = items?.find((x) => x.id === a.id);
    return fresh ? apiUrl(fresh[kind]) : null;
  };
  const onOpen = (e: MouseEvent<HTMLAnchorElement>, a: AttachmentDto) => {
    if (!refresh || !attachmentUrlNeedsRefresh(a.url)) return;
    e.preventDefault();
    const tab = window.open('', '_blank');
    void freshUrl(a, 'url').then((url) => {
      if (url && tab) tab.location.href = url;
      else {
        tab?.close();
        toast.error('No se pudo renovar el enlace', 'Vuelve a abrir la ficha e inténtalo de nuevo');
      }
    });
  };
  const onDownload = (e: MouseEvent<HTMLAnchorElement>, a: AttachmentDto) => {
    if (!refresh || !attachmentUrlNeedsRefresh(a.downloadUrl)) return;
    e.preventDefault();
    void freshUrl(a, 'downloadUrl').then((url) => {
      if (url) window.location.assign(url);
      else
        toast.error('No se pudo renovar el enlace', 'Vuelve a abrir la ficha e inténtalo de nuevo');
    });
  };

  const badgeFor = (a: AttachmentDto) =>
    targets.find((t) => t.entityType === a.entityType && t.entityId === a.entityId)?.badge ??
    ATTACHMENT_ENTITY_LABELS[a.entityType];

  return (
    <div className="space-y-3">
      {canUpload && target && (
        <div className="space-y-1.5" data-testid="attachment-upload">
          <div className="flex flex-wrap items-end gap-2">
            {targets.length > 1 && (
              <div className="min-w-[160px] flex-1 space-y-1">
                <Label htmlFor={`${id}-target`}>Guardar en</Label>
                <Select
                  id={`${id}-target`}
                  value={targetKey}
                  onChange={(e) => chooseTarget(e.target.value)}
                >
                  {targets.map((t) => (
                    <option key={keyOf(t)} value={keyOf(t)}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <div className="min-w-[140px] flex-1 space-y-1">
              <Label htmlFor={`${id}-category`}>Categoría</Label>
              <Select
                id={`${id}-category`}
                value={category}
                onChange={(e) => setCategory(e.target.value as AttachmentCategory)}
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {ATTACHMENT_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="min-w-[160px] flex-[2] space-y-1">
              <Label htmlFor={`${id}-description`}>Descripción</Label>
              <Input
                id={`${id}-description`}
                value={description}
                maxLength={300}
                placeholder="Opcional"
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div>
              <input
                ref={inputRef}
                type="file"
                accept={ATTACHMENT_ACCEPT}
                className="sr-only"
                aria-label="Archivo"
                data-testid="attachment-input"
                onChange={(e) => void onFile(e)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                disabled={upload.isPending}
                data-testid="btn-attachment-upload"
              >
                <Upload className="h-4 w-4" /> {upload.isPending ? 'Subiendo…' : 'Subir archivo'}
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground text-xs">
            PDF, imágenes, Word o Excel · máximo {ATTACHMENT_MAX_MB} MB por archivo.
          </p>
        </div>
      )}
      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : !items || items.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="attachments-empty">
          Sin archivos todavía.
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid="attachments-list">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 rounded-md border p-2"
              data-testid="attachment-row"
            >
              <a
                href={apiUrl(a.url)}
                target="_blank"
                rel="noreferrer"
                className="shrink-0"
                aria-label={`Abrir ${a.fileName}`}
                onClick={(e) => onOpen(e, a)}
              >
                {a.isImage ? (
                  <img
                    src={apiUrl(a.url)}
                    alt=""
                    className="h-10 w-10 rounded border object-cover"
                    loading="lazy"
                  />
                ) : (
                  <FileText className="text-muted-foreground h-8 w-8" />
                )}
              </a>
              <div className="min-w-0 flex-1">
                <a
                  href={apiUrl(a.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="block truncate text-sm font-medium hover:underline"
                  onClick={(e) => onOpen(e, a)}
                  data-testid="attachment-open"
                >
                  {a.fileName}
                </a>
                <p className="text-muted-foreground flex flex-wrap items-center gap-1 text-xs">
                  <Badge tone="outline">{ATTACHMENT_CATEGORY_LABELS[a.category]}</Badge>
                  {showTarget && <Badge tone="muted">{badgeFor(a)}</Badge>}
                  <span>
                    {formatFileSize(a.size)} · {fmt.date(a.createdAt)}
                    {a.uploadedBy ? ` · ${a.uploadedBy.name}` : ''}
                  </span>
                </p>
                {a.description && <p className="truncate text-xs">{a.description}</p>}
              </div>
              <a
                href={apiUrl(a.downloadUrl)}
                aria-label="Descargar"
                className="hover:bg-accent inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                onClick={(e) => onDownload(e, a)}
              >
                <Download className="h-4 w-4" />
              </a>
              {a.canDelete &&
                (confirming === a.id ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => void onDelete(a)}
                      disabled={remove.isPending}
                    >
                      Sí, borrar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                      No
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Borrar"
                    className="text-destructive shrink-0"
                    onClick={() => setConfirming(a.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
